import React, { ReactElement } from "react";
import { HStack } from "web/react/emo/h-stack";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { gettext } from "web/script/modules/django-i18n";
import { OrderLineSerializer } from "web/types/serializers";

const COST_LINE_TYPES = ["product", "shipping", "duties", "tax"] as const;

type CostLineType = (typeof COST_LINE_TYPES)[number];

function getCostLineLabel(lineType: CostLineType): string {
    switch (lineType) {
        case "product":
            return gettext("checkout.payment_summary.subtotal");
        case "shipping":
            return gettext("checkout.payment_summary.delivery");
        case "duties":
            return gettext("checkout.payment_summary.import_fees");
        case "tax":
            return gettext("checkout.payment_summary.tax");
        default:
            throw new Error("Unexpected cost line type");
    }
}

export interface RetailerOrderCostLinesProps {
    orderLines: OrderLineSerializer[];
}

export function RetailerOrderCostLines({
    orderLines,
}: RetailerOrderCostLinesProps): ReactElement | null {
    const amounts = new Map(orderLines.map((line) => [line.line_type, line.amount]));
    const costLines = COST_LINE_TYPES.filter((lineType) => amounts.has(lineType));

    if (costLines.length === 0) {
        return null;
    }

    return (
        <VStack spacing="xxs" dataTestId="retailer-order-cost-lines">
            {costLines.map((lineType) => (
                <HStack key={lineType} justify="space-between" dataTestId={`cost-line-${lineType}`}>
                    <Text as="p" textStyle="body-s">
                        {getCostLineLabel(lineType)}
                    </Text>
                    <Text as="p" textStyle="body-s">
                        {amounts.get(lineType)}
                    </Text>
                </HStack>
            ))}
        </VStack>
    );
}
